import {
  BridgeError,
  type AvailabilityProjection,
  type BookingActionName,
  type BookingBridge,
  type BookingChannel,
  type BookingCommand,
  type BookingProjection,
  type BookingStatus,
  type BookingStatusReport,
  type BlockingReason,
  type CheckoutSession,
  type CommandResult,
  type ContactProjection,
  type CreateFlowInput,
  type HotelBootstrap,
  type MinorGuest,
  type Money,
  type QuoteProjection,
  type QuoteRoomLine,
  type RoomCategoryAvailability,
  type RoomProjection,
  type SelectedAddon,
  type StayProjection,
} from "./contract";
import { DEFAULT_PUBLIC_KEY, FIXTURE_HOTELS } from "./fixtures/hotels";

/**
 * A stand-in for `CanonicalBookingCommerceService`.
 *
 * Everything commercial lives here, on purpose: capacity, availability, pricing, quote validity
 * and the decision about which action is allowed next. The flow in `src/flow` may read the
 * projection and send commands, nothing else. If a rule ever migrates out of this file into a
 * component, the widget has grown a second booking truth — which is precisely what the whole
 * plan exists to prevent.
 *
 * The numbers are invented. The *shape* of the answers is not: it is what the real services
 * will have to produce.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Pricing table — invented, deterministic, server-side only
// ─────────────────────────────────────────────────────────────────────────────

const NIGHTLY_BASE: Readonly<Record<string, number>> = {
  DBL: 129,
  STD: 140,
  SEA: 189,
  ACC: 150,
  BIZ: 119,
};

/** Per person per night, on top of the room. */
const MEAL_SUPPLEMENT: Readonly<Record<string, number>> = {
  EX: 0,
  BB: 18,
  HB: 42,
  FB: 62,
  AI: 89,
};

const ADDON_UNIT_PRICE: Readonly<Record<string, number>> = {
  BREAKFAST: 18,
  PARKING: 12,
  CRIB: 0,
  EXTRA_BED: 35,
  PET: 15,
  LATE_CHECKOUT: 40,
  EARLY_CHECKIN: 30,
  SPA: 25,
};

/** Share of the total taken at booking time. Mirrors what BookingFlowPolicy would decide. */
const DUE_NOW_PERCENT: Readonly<Record<string, number>> = {
  "HB-STD": 30,
  "FB-STD": 30,
  "AI-STD": 30,
};

const CITY_TAX_PER_PERSON_NIGHT: Readonly<Record<string, number>> = {
  DE: 2.5,
  ES: 2.2,
};

const QUOTE_TTL_MINUTES = 30;

// ─────────────────────────────────────────────────────────────────────────────
// Internal state
// ─────────────────────────────────────────────────────────────────────────────

type MockRoom = {
  roomId: string;
  adults: number;
  minors: { id: string; age: number | null; sleepsInParentBed: boolean }[];
  categoryCode: string | null;
  rateProductCode: string | null;
  requirements: string[];
  preferences: string[];
};

type MockAddon = {
  addonId: string;
  code: string;
  roomId: string | null;
  quantity: number;
  fromDate: string | null;
  toDate: string | null;
};

type FlowState = {
  flowId: string;
  publicKey: string;
  revision: number;
  channel: BookingChannel;
  language: string;
  stay: StayProjection | null;
  rooms: MockRoom[];
  addons: MockAddon[];
  contact: ContactProjection | null;
  quote: QuoteProjection | null;
  acceptedQuoteId: string | null;
  paymentState: string | null;
  reservationIds: string[];
  seq: number;
  autoSelectionNote: string | null;
};

export type MockBridgeOptions = {
  /** Injected so tests can move time without waiting. */
  now?: () => Date;
  /** Pre-selects the hotel when the loader did not pass a key. */
  defaultPublicKey?: string;
  /** Artificial latency in ms, so loading states are visible during development. */
  latencyMs?: number;
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const currencyFormatters = new Map<string, Intl.NumberFormat>();

function money(amount: number, currency: string): Money {
  let formatter = currencyFormatters.get(currency);
  if (!formatter) {
    formatter = new Intl.NumberFormat("de-DE", { style: "currency", currency });
    currencyFormatters.set(currency, formatter);
  }
  // Rounding happens here, once, on the server side of the boundary. The client receives a
  // finished string and can never re-derive or re-round it.
  return { formatted: formatter.format(Math.round(amount * 100) / 100), currency };
}

function parseDate(value: string): Date {
  return new Date(`${value}T00:00:00.000Z`);
}

function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function nightsBetween(checkin: string, checkout: string): number {
  const from = parseDate(checkin).getTime();
  const to = parseDate(checkout).getTime();
  return Math.round((to - from) / 86_400_000);
}

function stayDates(stay: StayProjection): string[] {
  const dates: string[] = [];
  const cursor = parseDate(stay.checkin);
  for (let i = 0; i < stay.nights; i += 1) {
    dates.push(toIsoDate(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return dates;
}

function isWeekend(date: string): boolean {
  const day = parseDate(date).getUTCDay();
  return day === 5 || day === 6;
}

/**
 * Whether a child consumes sleeping capacity.
 *
 * Only an infant young enough for the parent bed, whose party actually said so, is free. A
 * teenager needs a real or an extra bed. The age limit is the hotel's published rule, never a
 * number the widget picked.
 */
function countsAgainstCapacity(
  minor: { age: number | null; sleepsInParentBed: boolean },
  bootstrap: HotelBootstrap,
): boolean {
  const limit = bootstrap.rules.parentBedMaxAge;
  if (limit === null) return true;
  if (!minor.sleepsInParentBed) return true;
  if (minor.age === null) return true;
  return minor.age > limit;
}

function occupancyOf(room: MockRoom, bootstrap: HotelBootstrap): number {
  return room.adults + room.minors.filter((m) => countsAgainstCapacity(m, bootstrap)).length;
}

function personCountOf(room: MockRoom): number {
  return room.adults + room.minors.length;
}

// ─────────────────────────────────────────────────────────────────────────────
// Rules → blocking reasons
// ─────────────────────────────────────────────────────────────────────────────

function blockingReasonsFor(state: FlowState, bootstrap: HotelBootstrap): BlockingReason[] {
  const reasons: BlockingReason[] = [];

  if (!state.stay) {
    reasons.push({
      code: "STAY_REQUIRED",
      message: "Bitte wählen Sie An- und Abreise.",
      severity: "blocking",
      step: "stay",
      roomId: null,
      field: "checkin",
    });
  } else if (state.stay.nights > bootstrap.rules.maxNights) {
    reasons.push({
      code: "STAY_TOO_LONG",
      message: `Online buchbar sind bis zu ${bootstrap.rules.maxNights} Nächte. Für längere Aufenthalte melden Sie sich bitte direkt.`,
      severity: "blocking",
      step: "stay",
      roomId: null,
      field: "checkout",
    });
  }

  if (state.rooms.length > bootstrap.rules.maxRooms) {
    reasons.push({
      code: "TOO_MANY_ROOMS",
      message: `Online buchbar sind bis zu ${bootstrap.rules.maxRooms} Zimmer.`,
      severity: "blocking",
      step: "rooms",
      roomId: null,
      field: null,
    });
  }

  for (const room of state.rooms) {
    if (room.adults > bootstrap.rules.maxAdultsPerRoom) {
      reasons.push({
        code: "TOO_MANY_ADULTS",
        message: `In einem Zimmer sind bis zu ${bootstrap.rules.maxAdultsPerRoom} Erwachsene möglich.`,
        severity: "blocking",
        step: "party",
        roomId: room.roomId,
        field: "adults",
      });
    }

    if (bootstrap.rules.requiresMinorAges) {
      const missing = room.minors.some((m) => m.age === null);
      if (missing) {
        reasons.push({
          code: "MINOR_AGE_REQUIRED",
          message: "Bitte geben Sie das Alter an — davon hängen Preis und Bett ab.",
          severity: "blocking",
          step: "party",
          roomId: room.roomId,
          field: "minorAge",
        });
      }
    }

    const category = bootstrap.categories.find((c) => c.code === room.categoryCode);
    if (!category) {
      reasons.push({
        code: "CATEGORY_REQUIRED",
        message: "Bitte wählen Sie eine Zimmerkategorie.",
        severity: "blocking",
        step: "category",
        roomId: room.roomId,
        field: "categoryCode",
      });
    } else {
      const occupancy = occupancyOf(room, bootstrap);
      if (occupancy > category.maxOccupancy) {
        reasons.push({
          code: "CAPACITY_EXCEEDED",
          message: `${category.displayName} fasst ${category.maxOccupancy} Personen. Bitte verteilen Sie die Gäste auf ein weiteres Zimmer.`,
          severity: "blocking",
          step: "category",
          roomId: room.roomId,
          field: "categoryCode",
        });
      }
      if (room.adults > category.maxAdults) {
        reasons.push({
          code: "ADULT_CAPACITY_EXCEEDED",
          message: `${category.displayName} ist für ${category.maxAdults} Erwachsene ausgelegt.`,
          severity: "blocking",
          step: "category",
          roomId: room.roomId,
          field: "categoryCode",
        });
      }
      // A hard requirement is only satisfiable by a category that actually guarantees it.
      for (const code of room.requirements) {
        const guaranteed = category.guaranteedAttributes.some((a) => a.code === code);
        if (!guaranteed) {
          const requirement = bootstrap.requirements.find((r) => r.code === code);
          reasons.push({
            code: "REQUIREMENT_NOT_SATISFIED",
            message: `${requirement?.displayName ?? code}: ${category.displayName} kann das nicht zusagen. Bitte wählen Sie eine passende Kategorie.`,
            severity: "blocking",
            step: "category",
            roomId: room.roomId,
            field: "categoryCode",
          });
        }
      }
    }

    if (!room.rateProductCode) {
      reasons.push({
        code: "RATE_REQUIRED",
        message: "Bitte wählen Sie eine Rate.",
        severity: "blocking",
        step: "rate",
        roomId: room.roomId,
        field: "rateProductCode",
      });
    }
  }

  const contact = state.contact;
  if (!contact?.firstName || !contact.lastName || !contact.email) {
    reasons.push({
      code: "CONTACT_REQUIRED",
      message: "Bitte geben Sie Ihre Kontaktdaten an.",
      severity: "blocking",
      step: "contact",
      roomId: null,
      field: !contact?.email ? "email" : "firstName",
    });
  }
  if (contact?.isBusinessTrip && !contact.companyName) {
    reasons.push({
      code: "COMPANY_REQUIRED",
      message: "Für eine Geschäftsreise brauchen wir den Firmennamen für die Rechnung.",
      severity: "blocking",
      step: "contact",
      roomId: null,
      field: "companyName",
    });
  }

  return reasons;
}

// ─────────────────────────────────────────────────────────────────────────────
// Availability
// ─────────────────────────────────────────────────────────────────────────────

/** Lowest nightly price for a category over this stay, on the cheapest rate. */
function fromPriceFor(category: string, stay: StayProjection, currency: string): Money {
  const base = NIGHTLY_BASE[category] ?? 129;
  const cheapest = Math.min(...stayDates(stay).map((date) => base * (isWeekend(date) ? 1.15 : 1)));
  return money(cheapest, currency);
}

function availabilityFor(state: FlowState, bootstrap: HotelBootstrap, now: Date): AvailabilityProjection | null {
  if (!state.stay) return null;
  const stay = state.stay;
  // Deterministic scarcity so the "last room" and "limited" states are reachable in demos:
  // a stay starting on the 13th of a month makes the sea view category scarce.
  const scarce = parseDate(stay.checkin).getUTCDate() === 13;
  return {
    status: "available",
    resolvedAt: now.toISOString(),
    categories: bootstrap.categories.map((category) => ({
      code: category.code,
      status: scarce && category.code === "SEA" ? "limited" : "available",
      remainingHint: scarce && category.code === "SEA" ? "Noch 1 Zimmer frei" : null,
      fromPrice: fromPriceFor(category.code, stay, bootstrap.currency),
    })),
    autoSelectionNote: state.autoSelectionNote,
  };
}

/**
 * Which categories can take this room's party, and why not when they cannot.
 *
 * Answered before the guest clicks. A category that is going to be rejected should look
 * rejected on the card, not produce an error message after they picked it.
 */
function categoryOptionsFor(room: MockRoom, bootstrap: HotelBootstrap): RoomCategoryAvailability[] {
  const occupancy = occupancyOf(room, bootstrap);
  return bootstrap.categories.map((category) => {
    if (room.adults > category.maxAdults) {
      return {
        code: category.code,
        selectable: false,
        reason: `Für ${category.maxAdults} Erwachsene ausgelegt`,
      };
    }
    if (occupancy > category.maxOccupancy) {
      return {
        code: category.code,
        selectable: false,
        reason: `Fasst ${category.maxOccupancy} ${category.maxOccupancy === 1 ? "Person" : "Personen"}`,
      };
    }
    const missing = room.requirements.filter(
      (code) => !category.guaranteedAttributes.some((a) => a.code === code),
    );
    if (missing.length > 0) {
      const names = missing.map(
        (code) => bootstrap.requirements.find((r) => r.code === code)?.displayName ?? code,
      );
      return { code: category.code, selectable: false, reason: `Ohne ${names.join(", ")}` };
    }
    return { code: category.code, selectable: true, reason: null };
  });
}

function partyLabelFor(room: MockRoom): string {
  const parts = [`${room.adults} ${room.adults === 1 ? "Erwachsener" : "Erwachsene"}`];
  if (room.minors.length > 0) {
    const ages = room.minors.map((m) => (m.age === null ? "?" : String(m.age))).join(", ");
    parts.push(`${room.minors.length} ${room.minors.length === 1 ? "Kind" : "Kinder"} (${ages})`);
  }
  return parts.join(" · ");
}

/**
 * Fills in anything the configuration leaves only one answer for.
 *
 * Runs after every command, because a party change can turn three possible categories into one.
 * The choice is always reported back through `autoSelectionNote` — a selection the guest cannot
 * see happen is indistinguishable from a bug.
 */
function normalize(state: FlowState, bootstrap: HotelBootstrap): void {
  state.autoSelectionNote = null;
  for (const room of state.rooms) {
    if (bootstrap.rates.length === 1) room.rateProductCode = bootstrap.rates[0].code;

    if (room.categoryCode) {
      // A party or requirement change can invalidate a category the guest picked earlier.
      const stillFits = categoryOptionsFor(room, bootstrap).find((o) => o.code === room.categoryCode);
      if (stillFits && !stillFits.selectable && bootstrap.categories.length > 1) continue;
    }
    if (room.categoryCode) continue;

    const selectable = categoryOptionsFor(room, bootstrap).filter((o) => o.selectable);
    if (selectable.length === 1) {
      room.categoryCode = selectable[0].code;
      if (bootstrap.categories.length > 1) {
        const name = bootstrap.categories.find((c) => c.code === selectable[0].code)?.displayName ?? "";
        state.autoSelectionNote = `${name} ist die einzige Kategorie, die zu Ihrer Belegung passt — wir haben sie ausgewählt.`;
      }
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Quote
// ─────────────────────────────────────────────────────────────────────────────

function buildQuote(state: FlowState, bootstrap: HotelBootstrap, now: Date): QuoteProjection {
  const stay = state.stay;
  if (!stay) throw new BridgeError("BOOKING_REQUIREMENT_UNSATISFIED", "Kein Aufenthalt gesetzt");
  const currency = bootstrap.currency;
  const dates = stayDates(stay);

  let grossTotal = 0;
  const roomLines: QuoteRoomLine[] = state.rooms.map((room) => {
    const category = bootstrap.categories.find((c) => c.code === room.categoryCode);
    const rate = bootstrap.rates.find((r) => r.code === room.rateProductCode);
    if (!category || !rate) {
      throw new BridgeError("BOOKING_REQUIREMENT_UNSATISFIED", "Kategorie oder Rate fehlt");
    }
    const base = NIGHTLY_BASE[category.code] ?? 129;
    const supplement = MEAL_SUPPLEMENT[rate.mealPlanCode] ?? 0;
    const persons = personCountOf(room);
    let subtotal = 0;
    const nights = dates.map((date) => {
      const nightly = base * (isWeekend(date) ? 1.15 : 1) + supplement * persons;
      subtotal += nightly;
      return { date, price: money(nightly, currency) };
    });
    grossTotal += subtotal;
    return {
      roomId: room.roomId,
      categoryName: category.displayName,
      rateName: rate.displayName,
      mealPlanLabel: rate.mealPlanLabel,
      nights,
      includedBenefits: [...rate.includedBenefits],
      subtotal: money(subtotal, currency),
    };
  });

  const addonLines = state.addons.map((selected) => {
    const option = bootstrap.addons.find((a) => a.code === selected.code);
    const unit = ADDON_UNIT_PRICE[selected.code] ?? 0;
    const room = state.rooms.find((r) => r.roomId === selected.roomId) ?? state.rooms[0];
    const persons = room ? personCountOf(room) : 1;
    let multiplier = 1;
    switch (option?.pricingUnit) {
      case "per_night":
        multiplier = stay.nights;
        break;
      case "per_person":
        multiplier = persons;
        break;
      case "per_person_per_night":
        multiplier = persons * stay.nights;
        break;
      case "per_room_per_night":
        multiplier = stay.nights;
        break;
      default:
        multiplier = 1;
    }
    const total = unit * multiplier * selected.quantity;
    grossTotal += total;
    return {
      addonId: selected.addonId,
      displayName: option?.displayName ?? selected.code,
      quantityLabel: selected.quantity > 1 ? `${selected.quantity} ×` : "",
      total: money(total, currency),
    };
  });

  const guests = state.rooms.reduce((sum, room) => sum + personCountOf(room), 0);
  const cityTaxRate = CITY_TAX_PER_PERSON_NIGHT[bootstrap.hotel.countryCode ?? ""] ?? 0;
  const cityTax = cityTaxRate * guests * stay.nights;
  grossTotal += cityTax;

  const taxLines = cityTax
    ? [
        {
          label: "Kurtaxe",
          total: money(cityTax, currency),
          note: `${money(cityTaxRate, currency).formatted} pro Person und Nacht`,
        },
      ]
    : [];

  const firstRate = state.rooms[0]?.rateProductCode ?? "";
  const percent = DUE_NOW_PERCENT[firstRate] ?? 0;
  const grossCents = Math.round(grossTotal * 100);
  grossTotal = grossCents / 100;
  const dueNowCents = Math.round((grossCents * percent) / 100);
  const dueNow = dueNowCents / 100;
  const dueLater = (grossCents - dueNowCents) / 100;

  const rate = bootstrap.rates.find((r) => r.code === firstRate);
  const issued = now;
  const expires = new Date(issued.getTime() + QUOTE_TTL_MINUTES * 60_000);

  return {
    quoteId: `q_${state.flowId}_${state.seq}`,
    version: state.seq,
    fingerprint: `fp_${state.flowId}_${state.revision}_${Math.round(grossTotal * 100)}`,
    issuedAt: issued.toISOString(),
    expiresAt: expires.toISOString(),
    roomLines,
    addonLines,
    taxLines,
    total: money(grossTotal, currency),
    dueNow: money(dueNow, currency),
    dueLater: money(dueLater, currency),
    paymentSummary: percent
      ? `${percent} % Anzahlung jetzt, der Rest bei Anreise.`
      : (rate?.paymentSummary ?? "Zahlung bei Anreise."),
    cancellationSummary: rate?.cancellationSummary ?? "",
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Projection
// ─────────────────────────────────────────────────────────────────────────────

function statusOf(state: FlowState, reasons: BlockingReason[], now: Date): BookingStatus {
  if (state.reservationIds.length > 0) return "booked";
  if (state.paymentState === "paid") return "reservation_committing";
  if (state.paymentState === "pending") return "payment_pending";
  if (state.quote) {
    if (new Date(state.quote.expiresAt).getTime() <= now.getTime()) return "quote_expired";
    return state.acceptedQuoteId ? "checkout_ready" : "quote_ready";
  }
  if (reasons.length === 0) return "availability_ready";
  return "collecting";
}

function allowedActionsFor(
  state: FlowState,
  reasons: BlockingReason[],
  status: BookingStatus,
): BookingActionName[] {
  // After payment the widget is no longer the place to change a booking — the existing
  // amendment and cancellation policies take over.
  if (status === "paid" || status === "booked" || status === "reservation_committing") return [];

  const actions: BookingActionName[] = [
    "set_stay",
    "set_contact",
    "set_business_trip",
    "add_room",
    "update_room_party",
    "set_room_category",
    "set_room_rate",
    "set_room_requirement",
    "set_room_preference",
    "add_addon",
    "update_addon",
    "remove_addon",
  ];
  if (state.rooms.length > 1) actions.push("remove_room");
  if (reasons.length === 0 && !state.quote) actions.push("request_quote");
  if (status === "quote_ready") actions.push("accept_quote", "request_quote");
  if (status === "quote_expired") actions.push("request_quote");
  if (status === "checkout_ready") actions.push("create_checkout");
  return actions;
}

function projectMinor(
  minor: { id: string; age: number | null; sleepsInParentBed: boolean },
  bootstrap: HotelBootstrap,
): MinorGuest {
  return {
    id: minor.id,
    age: minor.age,
    sleepsInParentBed: minor.sleepsInParentBed,
    countsAgainstCapacity: countsAgainstCapacity(minor, bootstrap),
  };
}

function projectRoom(room: MockRoom, bootstrap: HotelBootstrap): RoomProjection {
  return {
    roomId: room.roomId,
    adults: room.adults,
    minors: room.minors.map((m) => projectMinor(m, bootstrap)),
    categoryCode: room.categoryCode,
    rateProductCode: room.rateProductCode,
    requirements: [...room.requirements],
    preferences: [...room.preferences],
    availabilityStatus: "available",
    categoryOptions: categoryOptionsFor(room, bootstrap),
    partyLabel: partyLabelFor(room),
  };
}

function projectAddon(selected: MockAddon, bootstrap: HotelBootstrap): SelectedAddon {
  const option = bootstrap.addons.find((a) => a.code === selected.code);
  return {
    addonId: selected.addonId,
    code: selected.code,
    displayName: option?.displayName ?? selected.code,
    scope: option?.scope ?? "booking",
    roomId: selected.roomId,
    quantity: selected.quantity,
    fromDate: selected.fromDate,
    toDate: selected.toDate,
    guaranteeLevel: option?.guaranteeLevel ?? "requested",
    priceStatus: option?.priceStatus ?? "priced",
  };
}

function project(state: FlowState, bootstrap: HotelBootstrap, now: Date): BookingProjection {
  const reasons = blockingReasonsFor(state, bootstrap);
  const status = statusOf(state, reasons, now);
  return {
    flowId: state.flowId,
    revision: state.revision,
    status,
    channel: state.channel,
    hotel: bootstrap.hotel,
    stay: state.stay,
    rooms: state.rooms.map((room) => projectRoom(room, bootstrap)),
    addons: state.addons.map((a) => projectAddon(a, bootstrap)),
    contact: state.contact,
    availability: availabilityFor(state, bootstrap, now),
    quote: status === "quote_expired" ? null : state.quote,
    payment: state.paymentState
      ? { state: state.paymentState, dueNow: state.quote?.dueNow ?? null, provider: "stripe" }
      : null,
    blockingReasons: reasons,
    allowedActions: allowedActionsFor(state, reasons, status),
    expiresAt: state.quote?.expiresAt ?? null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Command application
// ─────────────────────────────────────────────────────────────────────────────

/** Structural changes invalidate a quote. A contact correction does not. */
function invalidatesQuote(command: BookingCommand): boolean {
  switch (command.type) {
    case "set_contact":
    case "accept_quote":
      return false;
    default:
      return true;
  }
}

function applyCommandToState(state: FlowState, command: BookingCommand, bootstrap: HotelBootstrap): void {
  switch (command.type) {
    case "set_stay": {
      const nights = nightsBetween(command.checkin, command.checkout);
      if (nights <= 0) {
        throw new BridgeError("BOOKING_REQUIREMENT_UNSATISFIED", "Abreise muss nach der Anreise liegen");
      }
      state.stay = { checkin: command.checkin, checkout: command.checkout, nights };
      break;
    }
    case "set_contact": {
      state.contact = {
        firstName: command.firstName || null,
        lastName: command.lastName || null,
        email: command.email || null,
        phone: command.phone,
        isBusinessTrip: state.contact?.isBusinessTrip ?? false,
        companyName: state.contact?.companyName ?? null,
      };
      break;
    }
    case "set_business_trip": {
      state.contact = {
        firstName: state.contact?.firstName ?? null,
        lastName: state.contact?.lastName ?? null,
        email: state.contact?.email ?? null,
        phone: state.contact?.phone ?? null,
        isBusinessTrip: command.isBusinessTrip,
        companyName: command.isBusinessTrip ? command.companyName : null,
      };
      break;
    }
    case "add_room": {
      state.seq += 1;
      state.rooms.push({
        roomId: `room_${state.seq}`,
        adults: 2,
        minors: [],
        // A hotel with exactly one category or one rate has nothing to ask about, so the
        // choice is made here rather than shown as a step with a single button.
        categoryCode: bootstrap.categories.length === 1 ? bootstrap.categories[0].code : null,
        rateProductCode: bootstrap.rates.length === 1 ? bootstrap.rates[0].code : null,
        requirements: [],
        preferences: [],
      });
      break;
    }
    case "remove_room": {
      if (state.rooms.length <= 1) {
        throw new BridgeError("BOOKING_REQUIREMENT_UNSATISFIED", "Mindestens ein Zimmer ist nötig");
      }
      state.rooms = state.rooms.filter((r) => r.roomId !== command.roomId);
      state.addons = state.addons.filter((a) => a.roomId !== command.roomId);
      break;
    }
    case "update_room_party": {
      const room = state.rooms.find((r) => r.roomId === command.roomId);
      if (!room) throw new BridgeError("FLOW_NOT_FOUND", "Zimmer nicht gefunden");
      room.adults = Math.max(1, command.adults);
      room.minors = command.minors.map((minor, index) => ({
        id: `${room.roomId}_minor_${index + 1}`,
        age: minor.age,
        // The parent-bed option only exists where the hotel published an age limit for it.
        sleepsInParentBed:
          bootstrap.rules.parentBedMaxAge !== null &&
          minor.sleepsInParentBed &&
          minor.age !== null &&
          minor.age <= bootstrap.rules.parentBedMaxAge,
      }));
      break;
    }
    case "set_room_category": {
      const room = state.rooms.find((r) => r.roomId === command.roomId);
      if (!room) throw new BridgeError("FLOW_NOT_FOUND", "Zimmer nicht gefunden");
      if (!bootstrap.categories.some((c) => c.code === command.categoryCode)) {
        throw new BridgeError("BOOKING_REQUIREMENT_UNSATISFIED", "Unbekannte Kategorie");
      }
      room.categoryCode = command.categoryCode;
      break;
    }
    case "set_room_rate": {
      const room = state.rooms.find((r) => r.roomId === command.roomId);
      if (!room) throw new BridgeError("FLOW_NOT_FOUND", "Zimmer nicht gefunden");
      if (!bootstrap.rates.some((r) => r.code === command.rateProductCode)) {
        throw new BridgeError("BOOKING_REQUIREMENT_UNSATISFIED", "Unbekannte Rate");
      }
      if (state.addons.some((addon) => {
        const option = bootstrap.addons.find((candidate) => candidate.code === addon.code);
        return (addon.roomId === null || addon.roomId === room.roomId)
          && option?.conflictsWith.includes(command.rateProductCode);
      })) {
        throw new BridgeError("BOOKING_REQUIREMENT_UNSATISFIED", "Diese Leistung ist bereits in der Rate enthalten");
      }
      room.rateProductCode = command.rateProductCode;
      break;
    }
    case "set_room_requirement": {
      const room = state.rooms.find((r) => r.roomId === command.roomId);
      if (!room) throw new BridgeError("FLOW_NOT_FOUND", "Zimmer nicht gefunden");
      room.requirements = command.selected
        ? [...new Set([...room.requirements, command.optionCode])]
        : room.requirements.filter((c) => c !== command.optionCode);
      break;
    }
    case "set_room_preference": {
      const room = state.rooms.find((r) => r.roomId === command.roomId);
      if (!room) throw new BridgeError("FLOW_NOT_FOUND", "Zimmer nicht gefunden");
      room.preferences = command.selected
        ? [...new Set([...room.preferences, command.optionCode])]
        : room.preferences.filter((c) => c !== command.optionCode);
      break;
    }
    case "add_addon": {
      const option = bootstrap.addons.find((a) => a.code === command.code);
      if (!option) throw new BridgeError("BOOKING_REQUIREMENT_UNSATISFIED", "Unbekannte Zusatzleistung");
      const affectedRooms = state.rooms.filter((room) => command.roomId === null || command.roomId === room.roomId);
      if (affectedRooms.some((room) => room.rateProductCode && option.conflictsWith.includes(room.rateProductCode))) {
        throw new BridgeError("BOOKING_REQUIREMENT_UNSATISFIED", "Diese Leistung ist bereits in der Rate enthalten");
      }
      state.seq += 1;
      state.addons.push({
        addonId: `addon_${state.seq}`,
        code: command.code,
        roomId: command.roomId,
        quantity: option.maxQuantity ? Math.min(command.quantity, option.maxQuantity) : command.quantity,
        fromDate: command.fromDate,
        toDate: command.toDate,
      });
      break;
    }
    case "update_addon": {
      const existing = state.addons.find((a) => a.addonId === command.addonId);
      if (!existing) throw new BridgeError("FLOW_NOT_FOUND", "Zusatzleistung nicht gefunden");
      existing.quantity = Math.max(1, command.quantity);
      break;
    }
    case "remove_addon": {
      state.addons = state.addons.filter((a) => a.addonId !== command.addonId);
      break;
    }
    case "accept_quote": {
      if (!state.quote || state.quote.quoteId !== command.quoteId) {
        throw new BridgeError("BOOKING_REQUIREMENT_UNSATISFIED", "Angebot nicht mehr aktuell");
      }
      state.acceptedQuoteId = command.quoteId;
      break;
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Bridge
// ─────────────────────────────────────────────────────────────────────────────

const STORE_KEY = "exodus-booking-mock-store";

/**
 * Survives a page reload.
 *
 * Only the mock needs this — the real bridge reads the flow back from the server. Without it
 * "resume after reload" could not be evaluated at all in a demo, and that behaviour is one of
 * the things this build exists to let people judge.
 */
function loadStore(): { flows: Record<string, FlowState>; counter: number } {
  if (typeof sessionStorage === "undefined") return { flows: {}, counter: 0 };
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    if (!raw) return { flows: {}, counter: 0 };
    return JSON.parse(raw) as { flows: Record<string, FlowState>; counter: number };
  } catch {
    return { flows: {}, counter: 0 };
  }
}

function saveStore(flows: Map<string, FlowState>, counter: number): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify({ flows: Object.fromEntries(flows), counter }));
  } catch {
    // A full or disabled storage is not worth failing a booking over.
  }
}

export function createMockBridge(options: MockBridgeOptions = {}): BookingBridge {
  const now = options.now ?? (() => new Date());
  const latency = options.latencyMs ?? 0;
  const restored = loadStore();
  const flows = new Map<string, FlowState>(Object.entries(restored.flows));
  const idempotency = new Map<string, BookingProjection>();
  let activePublicKey = options.defaultPublicKey ?? DEFAULT_PUBLIC_KEY;
  let flowCounter = restored.counter;

  const persist = () => saveStore(flows, flowCounter);

  const wait = () => (latency ? new Promise<void>((resolve) => setTimeout(resolve, latency)) : Promise.resolve());

  function bootstrapFor(publicKey: string): HotelBootstrap {
    const found = FIXTURE_HOTELS[publicKey];
    if (!found) throw new BridgeError("SESSION_REJECTED", `Unbekannter Public Key: ${publicKey}`);
    return found;
  }

  function requireFlow(flowId: string): FlowState {
    const state = flows.get(flowId);
    if (!state) throw new BridgeError("FLOW_NOT_FOUND");
    return state;
  }

  return {
    async bootstrap(publicKey) {
      await wait();
      const resolved = publicKey || activePublicKey;
      const found = bootstrapFor(resolved);
      activePublicKey = resolved;
      return found;
    },

    async createFlow(input: CreateFlowInput) {
      await wait();
      const bootstrap = bootstrapFor(activePublicKey);
      flowCounter += 1;
      const state: FlowState = {
        flowId: `flow_${flowCounter}`,
        publicKey: activePublicKey,
        revision: 0,
        channel: input.channel,
        language: input.language,
        stay: null,
        rooms: [],
        addons: [],
        contact: null,
        quote: null,
        acceptedQuoteId: null,
        paymentState: null,
        reservationIds: [],
        seq: 0,
        autoSelectionNote: null,
      };
      // Every booking has at least one room; making the guest press "add room" first is a step
      // that answers nothing.
      applyCommandToState(state, { type: "add_room" }, bootstrap);
      normalize(state, bootstrap);
      flows.set(state.flowId, state);
      persist();
      return project(state, bootstrap, now());
    },

    async getFlow(flowId) {
      await wait();
      const state = requireFlow(flowId);
      return project(state, bootstrapFor(state.publicKey), now());
    },

    async applyCommand(flowId, revision, command, idempotencyKey): Promise<CommandResult> {
      await wait();
      const cached = idempotency.get(idempotencyKey);
      if (cached) return { kind: "ok", projection: cached };

      const state = requireFlow(flowId);
      const bootstrap = bootstrapFor(state.publicKey);

      if (revision !== state.revision) {
        return {
          kind: "revision_conflict",
          code: "REVISION_CONFLICT",
          current: project(state, bootstrap, now()),
        };
      }

      applyCommandToState(state, command, bootstrap);
      normalize(state, bootstrap);
      state.revision += 1;
      if (invalidatesQuote(command)) {
        state.quote = null;
        state.acceptedQuoteId = null;
      }

      const projection = project(state, bootstrap, now());
      idempotency.set(idempotencyKey, projection);
      persist();
      return { kind: "ok", projection };
    },

    async requestQuote(flowId, revision): Promise<CommandResult> {
      await wait();
      const state = requireFlow(flowId);
      const bootstrap = bootstrapFor(state.publicKey);
      if (revision !== state.revision) {
        return { kind: "revision_conflict", code: "REVISION_CONFLICT", current: project(state, bootstrap, now()) };
      }
      const reasons = blockingReasonsFor(state, bootstrap);
      if (reasons.length > 0) {
        // Fail closed: no quote is produced while anything is unresolved.
        return { kind: "ok", projection: project(state, bootstrap, now()) };
      }
      state.seq += 1;
      state.quote = buildQuote(state, bootstrap, now());
      state.acceptedQuoteId = null;
      persist();
      return { kind: "ok", projection: project(state, bootstrap, now()) };
    },

    async createCheckoutSession(flowId, quoteId): Promise<CheckoutSession> {
      await wait();
      const state = requireFlow(flowId);
      if (!state.quote || state.quote.quoteId !== quoteId || state.acceptedQuoteId !== quoteId) {
        throw new BridgeError("BOOKING_REQUIREMENT_UNSATISFIED", "Angebot nicht akzeptiert oder nicht mehr aktuell");
      }
      if (new Date(state.quote.expiresAt).getTime() <= now().getTime()) {
        throw new BridgeError("BOOKING_REQUIREMENT_UNSATISFIED", "Angebot abgelaufen");
      }
      state.paymentState = "pending";
      persist();
      return {
        checkoutSessionId: `cs_mock_${state.flowId}`,
        clientSecret: `cs_mock_secret_${state.flowId}`,
        publishableKey: "pk_test_widget_mock",
        mode: "sandbox",
        quoteFingerprint: state.quote.fingerprint,
        expiresAt: state.quote.expiresAt,
      };
    },

    async getStatus(flowId): Promise<BookingStatusReport> {
      await wait();
      const state = requireFlow(flowId);
      const bootstrap = bootstrapFor(state.publicKey);
      const projection = project(state, bootstrap, now());
      return {
        flowId,
        status: projection.status,
        reservationIds: [...state.reservationIds],
        message: null,
      };
    },
  };
}
